import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const { t, lang } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = !location.pathname.includes("/help");

  const anchors = [
    { href: isHome ? "#features" : `/${lang}#features`, label: t.nav.features, id: "nav-features" },
    { href: isHome ? "#videos" : `/${lang}#videos`, label: t.nav.videos, id: "nav-videos" },
    { href: isHome ? "#languages" : `/${lang}#languages`, label: t.nav.languages, id: "nav-languages" },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-sky-100/70 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-8">
        <Link to={`/${lang}`} data-testid="header-logo" className="flex items-center gap-2.5">
          <img src="/logo-app.png" alt="LingoMind" className="h-9 w-9 rounded-xl shadow-sm" />
          <span className="font-heading text-xl font-bold tracking-tight text-slate-900">
            Lingo<span className="text-sky-600">Mind</span>
            <span className="text-amber-500">.</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {anchors.map((a) => (
            <a
              key={a.id}
              data-testid={a.id}
              href={a.href}
              className="text-sm font-semibold text-slate-600 transition-colors hover:text-sky-700"
            >
              {a.label}
            </a>
          ))}
          <Link
            to={`/${lang}/help`}
            data-testid="nav-help"
            className="text-sm font-semibold text-slate-600 transition-colors hover:text-sky-700"
          >
            {t.nav.help}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <a
            data-testid="header-download-button"
            href="https://play.google.com/store/apps/details?id=com.lingomind.app&hl=en"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-full bg-sky-600 px-5 py-2 text-sm font-bold text-white shadow-lg shadow-sky-600/25 transition-all hover:bg-sky-700 hover:shadow-sky-600/40 md:block"
          >
            {t.nav.download}
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            className="rounded-lg p-2 text-slate-700 hover:bg-sky-50 lg:hidden"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-sky-100 bg-white/95 px-6 py-4 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-3">
            {anchors.map((a) => (
              <a
                key={a.id}
                data-testid={`mobile-${a.id}`}
                href={a.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-semibold text-slate-700"
              >
                {a.label}
              </a>
            ))}
            <Link
              to={`/${lang}/help`}
              data-testid="mobile-nav-help"
              onClick={() => setMobileOpen(false)}
              className="text-sm font-semibold text-slate-700"
            >
              {t.nav.help}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
